import React, { useState, useEffect } from 'react';
import {
    MdVisibility,
    MdVisibilityOff,
    MdSchedule,
    MdEdit,
    MdSave,
    MdCancel,
    MdCalendarToday,
    MdAccessTime,
    MdWarning,
    MdCheckCircle,
    MdSettings,
    MdAdd,
} from 'react-icons/md';
import { hasInstructorPermissions } from '../utils/permissions';
import { useClass } from '../utils/ClassContext';
import CreateEnvironmentModal from './CreateEnvironmentModal';

const EnvironmentVisibility = ({ userRole, apiBaseUrl, username }) => {
    const { getClassContext } = useClass();
    const [environments, setEnvironments] = useState([]);
    const [environmentSettings, setEnvironmentSettings] = useState({});
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState('');
    const [editingEnv, setEditingEnv] = useState(null);
    const [editForm, setEditForm] = useState({
        isVisible: false,
        openDate: '',
        closeDate: '',
        autoRelease: false
    });
    const [showCreateModal, setShowCreateModal] = useState(false);

    // Check permissions
    const hasPermission = hasInstructorPermissions(userRole);

    useEffect(() => {
        if (hasPermission) {
            fetchEnvironments();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [hasPermission, getClassContext]);

    const fetchEnvironments = async () => {
        setLoading(true);
        const classContext = getClassContext();
        
        // If no class is selected, set empty environments
        if (!classContext || !classContext.class_code) {
            setEnvironments([]);
            setEnvironmentSettings({});
            setLoading(false);
            return;
        }
        
        try {
            console.log('EnvironmentVisibility: fetchEnvironments called');
            console.log('Class context:', classContext);
            console.log('Class code being sent:', classContext.class_code);
            console.log('User role:', userRole);
            console.log('Username:', username);
            
            // Fetch available environments
            const envResponse = await fetch(`${apiBaseUrl}/environments`, {
                headers: {
                    'Content-Type': 'application/json',
                    'User-Role': userRole,
                    'Requesting-User': username,
                    'Class-Code': classContext.class_code
                },
                credentials: 'include'
            });

            console.log('Environment response status:', envResponse.status);
            
            if (envResponse.ok) {
                const envData = await envResponse.json();
                console.log('Environment response data:', envData);
                console.log('Environments received:', envData.environments);
                setEnvironments(envData.environments || []);

                // Fetch visibility settings for each environment
                const settingsPromises = envData.environments.filter(env => env !== 'None').map(async (env) => {
                    try {
                        const settingsResponse = await fetch(`${apiBaseUrl}/environment-visibility/${env}`, {
                            headers: {
                                'Content-Type': 'application/json',
                                'User-Role': userRole,
                                'Requesting-User': username,
                                'Class-Code': classContext.class_code
                            },
                            credentials: 'include'
                        });

                        if (settingsResponse.ok) {
                            const settingsData = await settingsResponse.json();
                            return { env, settings: settingsData };
                        }
                    } catch (err) {
                        console.error(`Error fetching settings for ${env}:`, err);
                    }
                    return { env, settings: getDefaultSettings() };
                });

                const allSettings = await Promise.all(settingsPromises);
                const settingsMap = {};
                allSettings.forEach(({ env, settings }) => {
                    settingsMap[env] = settings;
                });

                setEnvironmentSettings(settingsMap);
            }
        } catch (err) {
            console.error('Error fetching environments:', err);
            setError('Failed to load environments');
        } finally {
            setLoading(false);
        }
    };

    const getDefaultSettings = () => ({
        isVisible: false,
        openDate: null,
        closeDate: null,
        autoRelease: false,
        releasedBy: null,
        releasedAt: null,
        lastModifiedBy: null,
        lastModifiedAt: null
    });

    const startEditing = (env) => {
        const settings = environmentSettings[env] || getDefaultSettings();
        setEditingEnv(env);
        setEditForm({
            isVisible: settings.isVisible || false,
            openDate: settings.openDate ? new Date(settings.openDate).toISOString().slice(0, 16) : '',
            closeDate: settings.closeDate ? new Date(settings.closeDate).toISOString().slice(0, 16) : '',
            autoRelease: settings.autoRelease || false
        });
    };

    const cancelEditing = () => {
        setEditingEnv(null);
        setEditForm({
            isVisible: false,
            openDate: '',
            closeDate: '',
            autoRelease: false
        });
    };

    const saveSettings = async () => {
        if (!editingEnv) return;

        setSaving(true);
        try {
            const settingsToSave = {
                isVisible: editForm.isVisible,
                openDate: editForm.openDate ? new Date(editForm.openDate).toISOString() : null,
                closeDate: editForm.closeDate ? new Date(editForm.closeDate).toISOString() : null,
                autoRelease: editForm.autoRelease,
                lastModifiedBy: username,
                lastModifiedAt: new Date().toISOString()
            };

            // If manually setting visible, record who released it
            if (editForm.isVisible && !environmentSettings[editingEnv]?.isVisible) {
                settingsToSave.releasedBy = username;
                settingsToSave.releasedAt = new Date().toISOString();
            }

            const classContext = getClassContext();
            const response = await fetch(`${apiBaseUrl}/environment-visibility/${editingEnv}`, {
                method: 'PUT',
                headers: {
                    'Content-Type': 'application/json',
                    'User-Role': userRole,
                    'Requesting-User': username,
                    'Class-Code': classContext.class_code
                },
                credentials: 'include',
                body: JSON.stringify(settingsToSave)
            });

            if (response.ok) {
                setEnvironmentSettings(prev => ({
                    ...prev,
                    [editingEnv]: settingsToSave
                }));
                setEditingEnv(null);
                setEditForm({
                    isVisible: false,
                    openDate: '',
                    closeDate: '',
                    autoRelease: false
                });
            } else {
                throw new Error('Failed to save settings');
            }
        } catch (err) {
            console.error('Error saving settings:', err);
            setError('Failed to save settings');
        } finally {
            setSaving(false);
        }
    };

    const quickToggleVisibility = async (env) => {
        try {
            const currentSettings = environmentSettings[env] || getDefaultSettings();
            const newVisibility = !currentSettings.isVisible;

            const settingsToSave = {
                ...currentSettings,
                isVisible: newVisibility,
                lastModifiedBy: username,
                lastModifiedAt: new Date().toISOString()
            };

            // If making visible, record who released it
            if (newVisibility) {
                settingsToSave.releasedBy = username;
                settingsToSave.releasedAt = new Date().toISOString();
            }

            const classContext = getClassContext();
            const response = await fetch(`${apiBaseUrl}/environment-visibility/${env}`, {
                method: 'PUT',
                headers: {
                    'Content-Type': 'application/json',
                    'User-Role': userRole,
                    'Requesting-User': username,
                    'Class-Code': classContext.class_code
                },
                credentials: 'include',
                body: JSON.stringify(settingsToSave)
            });

            if (response.ok) {
                setEnvironmentSettings(prev => ({
                    ...prev,
                    [env]: settingsToSave
                }));
            } else {
                throw new Error('Failed to update visibility');
            }
        } catch (err) {
            console.error('Error toggling visibility:', err);
            setError('Failed to update visibility');
        }
    };

    const getVisibilityStatus = (env) => {
        const settings = environmentSettings[env];
        if (!settings) return { status: 'hidden', message: 'Not configured' };

        const now = new Date();

        // Manual visibility override
        if (settings.isVisible && !settings.autoRelease) {
            return { status: 'visible', message: 'Manually released' };
        }

        // Auto-release based on dates
        if (settings.autoRelease && settings.openDate && settings.closeDate) {
            const openDate = new Date(settings.openDate);
            const closeDate = new Date(settings.closeDate);

            if (now < openDate) {
                return {
                    status: 'scheduled',
                    message: `Opens ${openDate.toLocaleDateString()} at ${openDate.toLocaleTimeString()}`
                };
            } else if (now >= openDate && now <= closeDate) {
                return { status: 'visible', message: 'Auto-released (active)' };
            } else if (now > closeDate) {
                return { status: 'expired', message: 'Auto-released (expired)' };
            }
        }

        return { status: 'hidden', message: 'Hidden from students' };
    };

    const getStatusIcon = (status) => {
        switch (status) {
            case 'visible':
                return <MdCheckCircle className="text-green-600 text-xl" />;
            case 'scheduled':
                return <MdSchedule className="text-blue-600 text-xl" />;
            case 'expired':
                return <MdWarning className="text-orange-600 text-xl" />;
            default:
                return <MdVisibilityOff className="text-gray-600 text-xl" />;
        }
    };

    const getStatusColor = (status) => {
        switch (status) {
            case 'visible':
                return 'bg-green-50 border-green-200 text-green-800';
            case 'scheduled':
                return 'bg-blue-50 border-blue-200 text-blue-800';
            case 'expired':
                return 'bg-orange-50 border-orange-200 text-orange-800';
            default:
                return 'bg-gray-50 border-gray-200 text-gray-800';
        }
    };

    if (!hasPermission) {
        return (
            <div className="max-w-4xl mx-auto space-y-6">
                <div className="bg-white rounded-lg shadow-lg p-6">
                    <div className="text-center py-8">
                        <MdSettings className="text-6xl text-gray-400 mx-auto mb-4" />
                        <h2 className="text-2xl font-bold text-gray-800 mb-2">Access Denied</h2>
                        <p className="text-gray-600">
                            You need instructor or TA privileges to manage environment visibility.
                        </p>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="max-w-6xl mx-auto space-y-6">
            {/* Header */}
            <div className="bg-white rounded-lg shadow-lg p-6">
                <div className="flex items-center justify-between">
                    <div>
                        <h1 className="text-3xl font-bold text-gray-800 mb-2">Environment Visibility</h1>
                        <p className="text-gray-600">
                            Control when students can see environment requirements. Set manual release or automatic scheduling.
                        </p>
                    </div>
                    <div className="flex items-center space-x-4">
                        <button
                            onClick={() => setShowCreateModal(true)}
                            className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 transition-colors font-medium"
                        >
                            <MdAdd className="inline mr-1" />
                            Create Environment
                        </button>
                        <div className="hidden md:block">
                            <div className="w-16 h-16 bg-gradient-to-br from-green-500 to-blue-500 rounded-full flex items-center justify-center">
                                <MdSettings className="text-white text-2xl" />
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Error Display */}
            {error && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                    <div className="flex items-center">
                        <MdWarning className="text-red-600 text-xl mr-3" />
                        <p className="text-red-800">{error}</p>
                    </div>
                </div>
            )}

            {/* Loading State */}
            {loading ? (
                <div className="bg-white rounded-lg shadow-lg p-6">
                    <div className="flex items-center justify-center py-12">
                        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
                        <span className="ml-3 text-gray-600">Loading environments...</span>
                    </div>
                </div>
            ) : (
                /* Environments List */
                <div className="space-y-4">
                    {environments.filter(env => env !== 'None').length === 0 ? (
                        <div className="bg-white rounded-lg shadow-lg p-6">
                            <div className="text-center py-12">
                                <MdWarning className="mx-auto h-12 w-12 text-yellow-500 mb-4" />
                                <h3 className="text-lg font-medium text-gray-800 mb-2">No Environments Available</h3>
                                <p className="text-gray-600">
                                    No environments are available for the selected class. Please select a class that has environments configured.
                                </p>
                            </div>
                        </div>
                    ) : (
                        environments.filter(env => env !== 'None').map((env) => {
                        const settings = environmentSettings[env] || getDefaultSettings();
                        const { status, message } = getVisibilityStatus(env);
                        const isEditing = editingEnv === env;

                        return (
                            <div key={env} className="bg-white rounded-lg shadow-lg p-6">
                                <div className="flex items-center justify-between mb-4">
                                    <div className="flex items-center space-x-3">
                                        <h3 className="text-xl font-semibold text-gray-800">{env}</h3>
                                        {getStatusIcon(status)}
                                    </div>

                                    <div className="flex items-center space-x-2">
                                        {!isEditing && (
                                            <>
                                                <button
                                                    onClick={() => quickToggleVisibility(env)}
                                                    className={`px-3 py-1 rounded-md text-sm font-medium transition-colors ${settings.isVisible
                                                        ? 'bg-red-100 text-red-700 hover:bg-red-200'
                                                        : 'bg-green-100 text-green-700 hover:bg-green-200'
                                                        }`}
                                                >
                                                    {settings.isVisible ? (
                                                        <>
                                                            <MdVisibilityOff className="inline mr-1" />
                                                            Hide
                                                        </>
                                                    ) : (
                                                        <>
                                                            <MdVisibility className="inline mr-1" />
                                                            Release
                                                        </>
                                                    )}
                                                </button>
                                                <button
                                                    onClick={() => startEditing(env)}
                                                    className="px-3 py-1 bg-blue-100 text-blue-700 rounded-md text-sm font-medium hover:bg-blue-200 transition-colors"
                                                >
                                                    <MdEdit className="inline mr-1" />
                                                    Configure
                                                </button>
                                            </>
                                        )}
                                    </div>
                                </div>

                                {/* Status Display */}
                                <div className={`border rounded-lg p-3 mb-4 ${getStatusColor(status)}`}>
                                    <p className="font-medium">Status: {message}</p>
                                    {settings.releasedBy && (
                                        <p className="text-sm mt-1">
                                            Released by {settings.releasedBy} on {new Date(settings.releasedAt).toLocaleDateString()}
                                        </p>
                                    )}
                                </div>

                                {/* Edit Form */}
                                {isEditing && (
                                    <div className="border-t pt-4">
                                        <div className="grid md:grid-cols-2 gap-4">
                                            {/* Manual Visibility */}
                                            <div className="space-y-3">
                                                <h4 className="font-medium text-gray-800">Manual Control</h4>
                                                <label className="flex items-center space-x-2">
                                                    <input
                                                        type="checkbox"
                                                        checked={editForm.isVisible}
                                                        onChange={(e) => setEditForm(prev => ({
                                                            ...prev,
                                                            isVisible: e.target.checked
                                                        }))}
                                                        className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                                                    />
                                                    <span className="text-sm text-gray-700">
                                                        Manually release this environment
                                                    </span>
                                                </label>
                                            </div>

                                            {/* Automatic Scheduling */}
                                            <div className="space-y-3">
                                                <h4 className="font-medium text-gray-800">Automatic Scheduling</h4>
                                                <label className="flex items-center space-x-2">
                                                    <input
                                                        type="checkbox"
                                                        checked={editForm.autoRelease}
                                                        onChange={(e) => setEditForm(prev => ({
                                                            ...prev,
                                                            autoRelease: e.target.checked
                                                        }))}
                                                        className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                                                    />
                                                    <span className="text-sm text-gray-700">
                                                        Enable automatic release/hide based on dates
                                                    </span>
                                                </label>
                                            </div>
                                        </div>

                                        {/* Date Inputs */}
                                        {editForm.autoRelease && (
                                            <div className="grid md:grid-cols-2 gap-4 mt-4">
                                                <div>
                                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                                        <MdCalendarToday className="inline mr-1" />
                                                        Open Date & Time
                                                    </label>
                                                    <input
                                                        type="datetime-local"
                                                        value={editForm.openDate}
                                                        onChange={(e) => setEditForm(prev => ({
                                                            ...prev,
                                                            openDate: e.target.value
                                                        }))}
                                                        className="w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-2 focus:ring-blue-500"
                                                    />
                                                </div>

                                                <div>
                                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                                        <MdAccessTime className="inline mr-1" />
                                                        Close Date & Time
                                                    </label>
                                                    <input
                                                        type="datetime-local"
                                                        value={editForm.closeDate}
                                                        onChange={(e) => setEditForm(prev => ({
                                                            ...prev,
                                                            closeDate: e.target.value
                                                        }))}
                                                        className="w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-2 focus:ring-blue-500"
                                                    />
                                                </div>
                                            </div>
                                        )}

                                        {/* Action Buttons */}
                                        <div className="flex justify-end space-x-3 mt-6">
                                            <button
                                                onClick={cancelEditing}
                                                className="px-4 py-2 text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200 transition-colors"
                                            >
                                                <MdCancel className="inline mr-1" />
                                                Cancel
                                            </button>
                                            <button
                                                onClick={saveSettings}
                                                disabled={saving}
                                                className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:bg-blue-400 transition-colors"
                                            >
                                                {saving ? (
                                                    <>
                                                        <div className="inline-block animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-1"></div>
                                                        Saving...
                                                    </>
                                                ) : (
                                                    <>
                                                        <MdSave className="inline mr-1" />
                                                        Save Settings
                                                    </>
                                                )}
                                            </button>
                                        </div>
                                    </div>
                                )}
                            </div>
                        );
                    })
                    )}
                </div>
            )}

            {/* Create Environment Modal */}
            <CreateEnvironmentModal
                isOpen={showCreateModal}
                onClose={() => setShowCreateModal(false)}
                onSuccess={() => {
                    fetchEnvironments();
                    setShowCreateModal(false);
                }}
                userRole={userRole}
                apiBaseUrl={apiBaseUrl}
                username={username}
            />
        </div>
    );
};

export default EnvironmentVisibility;